export interface HttpErrorLike {
  status?: number;
  statusCode?: number;
  response?: {
    status?: number;
  };
}

export const getHttpStatusFromError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const maybeError = error as HttpErrorLike;

  return maybeError.status ?? maybeError.statusCode ?? maybeError.response?.status;
};

export const isAuthenticationError = (error: unknown): boolean => getHttpStatusFromError(error) === 401;

export const isAuthorizationError = (error: unknown): boolean => getHttpStatusFromError(error) === 403;

const UNREACHABLE_STATUSES = [502, 503, 504];

// The server never produced an answer the user can act on: the connection failed, timed out, or a gateway
// replied for it. A gateway timeout often has no CORS headers, so the browser reports it as a terminated
// request (superagent's `crossDomain`) rather than as a 504. Aborts are the user's own and are excluded.
export const isRequestUnreachableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { crossDomain, code } = error as { crossDomain?: boolean; code?: string };
  const status = getHttpStatusFromError(error);

  return (
    crossDomain === true || code === 'ECONNABORTED' || (status !== undefined && UNREACHABLE_STATUSES.includes(status))
  );
};

export const getQueryErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (!error || typeof error !== 'object') {
    return fallbackMessage;
  }

  const maybeError = error as {
    body?: { detail?: string; message?: string };
    response?: { body?: { detail?: string; message?: string } };
    message?: string;
  };

  if (typeof maybeError.response?.body?.message === 'string' && maybeError.response.body.message) {
    return maybeError.response.body.message;
  }

  if (typeof maybeError.response?.body?.detail === 'string' && maybeError.response.body.detail) {
    return maybeError.response.body.detail;
  }

  if (typeof maybeError.body?.message === 'string' && maybeError.body.message) {
    return maybeError.body.message;
  }

  if (typeof maybeError.body?.detail === 'string' && maybeError.body.detail) {
    return maybeError.body.detail;
  }

  if (typeof maybeError.message === 'string' && maybeError.message) {
    return maybeError.message;
  }

  return fallbackMessage;
};
